import { EPlayerType } from '@customTypes/common';

export enum EReportType {
  PLANNED = 1,
  PRECLOSE = 2,
  PRECLEAR = 3,
}

export interface IRistrettoMetricsReport {
  framesDecoded: number;
  nackCount: number;
  totalFreezesDuration: number;
  totalPausesDuration: number;
  fecPacketsReceived: number;
  fecPacketsDiscarded: number;
  framesReceived: number;
  packetsLost: number;
  packetsDiscarded: number;
  packetsReceived: number;
  maxRTT: number;
  medianRTT: number;
  maxJitter: number;
  medianJitter: number;
}

export interface IRistrettoMetricsCollected {
  framesDecoded: number;
  nackCount: number;
  totalFreezesDuration: number;
  totalPausesDuration: number;
  fecPacketsDiscarded: number;
  fecPacketsReceived: number;
  framesReceived: number;
  audioJitter: number;
  videoJitter: number;
  packetsLost: number;
  packetsDiscarded: number;
  packetsReceived: number;
  totalRoundTripTime: number;
  responsesReceived: number;
}

export interface IRistrettoBroadcastMetricsCollected {
  framesSent: number;
  framesEncoded: number;
}

export interface IDoppioMetricsCollected {
  targetBuffer: number;
  chunkLoadDuration: number;
  chunkLoadTTFB: number;
  playlistLoadDuration: number;
  playlistLoadTTFB: number;
  chunkSize: number;
  playlistSize: number;
}

export interface IMMPMetricsCollected {
  totalFramesDecoded: number;
  totalFramesDropped: number;
  buffer: number;
  latency: number;
  upscaleFactorMax: number;
  upscaleFactorAvg: number;
}

export interface IDoppioMetricsReported {
  maxTargetBuffer: number;
  medianTargetBuffer: number;
  maxChunkLoadDuration: number;
  medianChunkLoadDuration: number;
  maxChunkLoadTTFB: number;
  medianChunkLoadTTFB: number;
  maxPlaylistLoadDuration: number;
  medianPlaylistLoadDuration: number;
  maxPlaylistLoadTTFB: number;
  medianPlaylistLoadTTFB: number;
}

export interface IMMPMetricsReport {
  totalFramesDecoded: number;
  totalFramesDropped: number;
  currentTimestamp: number;
  fps: number;
  maxBuffer: number;
  medianBuffer: number;
  upscaleFactorAvg: number;
  upscaleFactorMax: number;
}

export type TMetricsReport = Partial<
  IMMPMetricsReport & IDoppioMetricsReported & IRistrettoMetricsReport
>;

export type TMetricsCollected = Partial<
  IMMPMetricsCollected &
    IDoppioMetricsCollected &
    IRistrettoMetricsCollected &
    IRistrettoBroadcastMetricsCollected
>;

export type TMetricsBodyWithMeta = TMetricsReport & {
  userId: string | number;
  sessionStartTime: number;
  streamId: string;
  timestamp: number;
  playerType: EPlayerType;
  playerImpl: 'MMP';
  projectId: string;
  cdnDomain: string;
  reportType: EReportType;
  abTests: Record<string, string>;
  abTestsSupport: Record<string, boolean>;
  mmpVersion: string;
  userType?: string;
};

export type TDoppioEventType =
  | 'ManifestLoaded'
  | 'FirstMediaPlaylistLoaded'
  | 'FirstFragmentLoaded'
  | 'FirstFragmentBuffered'
  | 'RenditionSwitchedAuto'
  | 'ManifestLoadError'
  | 'FragmentFilteredOut'
  | 'PlaylistLoadError'
  | 'FragmentLoadError';

export type TRistrettoEventType =
  | 'SocketOpened'
  | 'SDPReceived'
  | 'PeerConnectionEstablished'
  | 'SignallingEstablishError'
  | 'SignallingConnectionError'
  | 'SignallingErrorRecoverableMessageReceived'
  | 'SignallingErrorUnrecoverableMessageReceived'
  | 'PeerEstablishError'
  | 'RenditionSwitchedAuto';

export type TMMPEventType =
  | 'CustomEvent'
  | 'SessionStarted'
  | 'ReadyToPlay'
  | 'Played'
  | 'Paused'
  | 'StalledNative'
  | 'Stalled'
  | 'TabBlurred'
  | 'TabFocused'
  | 'RenditionSwitchedManual'
  | 'RenditionSelectModeSwitched'
  | 'PlayerTypeSwitched'
  | 'FullscreenEntered'
  | 'FullscreenExited'
  | 'MediaDecodingError'
  | 'SessionTerminated'
  | 'PageReloaded'
  | 'OngoingSessionDetected'
  | 'MediaApiTamperingReported'
  | 'MediaApiTamperingForbidden'
  | 'WrongPlayerActive';

export type TEventType = TDoppioEventType | TRistrettoEventType | TMMPEventType;

export interface IEventDetails {
  prevResolution?: string;
  nextResolution?: string;
  prevMode?: 'auto' | 'manual';
  nextMode?: 'auto' | 'manual';
  prevPlayerType?: EPlayerType;
  nextPlayerType?: EPlayerType;
  errorMessage?: string;
  reason?: string;
  fragmentName?: string;
  customEventName?: string;
  wrongPlayerType?: EPlayerType;
  activePlayerType?: EPlayerType | null;
}

export type TEvent = {
  eventType: TEventType;
  timestamp: number;
  userId: string | number;
  sessionStartTime: number;
  streamId: string;
  playerImpl: string;
  playerType: EPlayerType;
  projectId: string;
  payload: IEventDetails;
  cdnDomain: string;
  abTests: Record<string, string>;
  abTestsSupport: Record<string, boolean>;
  mmpVersion: string;
  sequenceNumber: number;
  userType?: string;
};

export interface IFetchRequestOptions {
  url: string;
  data:
    | TMetricsBodyWithMeta
    | {
        events: TEvent[];
      };
  onError?: (e: Error) => void;
  onSuccess?: () => void;
  isTerminating?: boolean;
}
